import React, { useEffect, useMemo, useState } from 'react'
import { Checkbox, Form, Input, InputNumber, Radio } from 'antd'
import dayjs from 'dayjs'
import { useRequest } from 'ahooks'
import { Modal } from '@/components/dialog/modal'
import $msg from '@/utils/message'
import { getShopPayment, loadMemberSettingConfig, rechargeMember } from '../api'
import {
    getEnabledRechargePaymentOptions,
    getActiveRechargeBonusLevelByAmount,
    getActiveRechargeBonusLevels,
    getRechargeBonusAmountAfterAmountChange,
    getRechargeBonusSubmitParams,
} from './recharge-member-utils'

interface RechargeMemberForm {
    amount: number
    bonus_amount?: number
    payment?: number
    send_sms_to_customer?: boolean
    remark?: string
}

interface Props {
    visible: boolean
    memberId: number
    onCancel: () => void
    onSuccess: () => void
}

const RechargeMemberModal: React.FC<Props> = props => {
    const [form] = Form.useForm<RechargeMemberForm>()
    const [loading, setLoading] = useState(false)
    const {
        run: loadRechargeBonus,
        data: rechargeBonusData,
        loading: rechargeBonusLoading,
        error: rechargeBonusError,
    } = useRequest(() => loadMemberSettingConfig('/activity/getrechargebonusactivity'), {
        manual: true,
        onError: e => $msg('error', e.message || '获取充值赠送档位失败'),
    })
    const {
        run: loadShopPayment,
        data: shopPaymentData,
        loading: shopPaymentLoading,
        error: shopPaymentError,
    } = useRequest(getShopPayment, {
        manual: true,
        onError: e => $msg('error', e.message || '获取支付方式失败'),
    })
    const rechargeBonusLevels = useMemo(() => getActiveRechargeBonusLevels(rechargeBonusData), [rechargeBonusData])
    const paymentOptions = useMemo(
        () => getEnabledRechargePaymentOptions(shopPaymentData?.paymentconfig?.payment_list),
        [shopPaymentData],
    )

    useEffect(() => {
        if (!props.visible) return

        loadRechargeBonus()
        loadShopPayment()
        form.setFieldsValue({
            amount: undefined,
            bonus_amount: 0,
            send_sms_to_customer: false,
            remark: '',
        })
    }, [form, loadRechargeBonus, loadShopPayment, props.visible])

    useEffect(() => {
        const activeLevel = getActiveRechargeBonusLevelByAmount(rechargeBonusLevels, form.getFieldValue('amount'))
        if (activeLevel) form.setFieldsValue({ bonus_amount: Number(activeLevel.bonus_amount) })
    }, [form, rechargeBonusLevels])

    useEffect(() => {
        if (!props.visible) return
        if (!paymentOptions.length) return

        const currentPayment = form.getFieldValue('payment')
        if (paymentOptions.some(option => option.value === currentPayment)) return

        form.setFieldsValue({ payment: paymentOptions[0].value })
    }, [form, paymentOptions, props.visible])

    const onValuesChange = (changedValues: Partial<RechargeMemberForm>) => {
        if (!Object.prototype.hasOwnProperty.call(changedValues, 'amount')) return

        form.setFieldsValue({
            bonus_amount: getRechargeBonusAmountAfterAmountChange(changedValues.amount, rechargeBonusLevels),
        })
    }

    const onSubmit = async () => {
        try {
            if (rechargeBonusLoading) {
                $msg('warning', '充值赠送档位加载中，请稍后')

                return
            }
            if (rechargeBonusError) {
                $msg('error', '充值赠送档位获取失败，请关闭弹窗后重试')

                return
            }
            if (shopPaymentLoading) {
                $msg('warning', '支付方式加载中，请稍后')

                return
            }
            if (shopPaymentError) {
                $msg('error', '支付方式获取失败，请关闭弹窗后重试')

                return
            }
            if (!shopPaymentData) {
                $msg('warning', '支付方式加载中，请稍后')

                return
            }
            if (!paymentOptions.length) {
                $msg('warning', '暂无可用支付方式')

                return
            }
            const values = await form.validateFields()
            const rechargeBonusParams = getRechargeBonusSubmitParams(values, rechargeBonusLevels)
            setLoading(true)
            await rechargeMember({
                member_id: props.memberId,
                serial_number: String(dayjs().valueOf()),
                amount: values.amount,
                bonus_amount: rechargeBonusParams.bonus_amount,
                activity_level_id: rechargeBonusParams.activity_level_id,
                remark: values.remark,
                send_sms_to_customer: !!values.send_sms_to_customer,
                payments: [
                    {
                        payment: values.payment as number,
                        amount: values.amount,
                    },
                ],
            })
            $msg('success', '手动充值成功')
            props.onSuccess()
        } catch (e) {
            if (e && e.errorFields) return
            $msg('error', e.message || '手动充值失败')
        } finally {
            setLoading(false)
        }
    }

    return props.visible ? (
        <Modal
            title="手动充值"
            isShow
            type="success"
            loading={loading}
            onConfirm={onSubmit}
            onClose={props.onCancel}
            onCancel={props.onCancel}
            okText="确认充值"
            cancelText="取消"
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ payment: 0, bonus_amount: 0 }}
                onValuesChange={onValuesChange}
            >
                <Form.Item
                    name="amount"
                    label="充值金额"
                    rules={[
                        { required: true, message: '请输入充值金额' },
                        {
                            validator: (_, value) =>
                                value > 0 ? Promise.resolve() : Promise.reject(new Error('充值金额必须大于0')),
                        },
                    ]}
                >
                    <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入充值金额" />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, next) => prev.amount !== next.amount}>
                    {() => {
                        const activeLevel = getActiveRechargeBonusLevelByAmount(
                            rechargeBonusLevels,
                            form.getFieldValue('amount'),
                        )

                        return (
                            <Form.Item name="bonus_amount" label="赠送金额">
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    disabled={!!activeLevel}
                                    style={{ width: '100%' }}
                                    placeholder="请输入赠送金额"
                                />
                            </Form.Item>
                        )
                    }}
                </Form.Item>
                <Form.Item name="payment" label="支付方式" rules={[{ required: true, message: '请选择支付方式' }]}>
                    <Radio.Group options={paymentOptions} disabled={shopPaymentLoading || !!shopPaymentError} />
                </Form.Item>
                <Form.Item name="send_sms_to_customer" valuePropName="checked">
                    <Checkbox>发送短信通知会员</Checkbox>
                </Form.Item>
                <Form.Item name="remark" label="备注" rules={[{ max: 50, message: '备注最多50个字' }]}>
                    <Input placeholder="请输入备注" />
                </Form.Item>
            </Form>
        </Modal>
    ) : null
}

export default RechargeMemberModal
